/**
 * useMentionables — who can be @mentioned in this workspace
 * (track:mention-roster, spec #51157).
 *
 * Fetches GET /api/workspace/mentionables on mount and whenever the
 * workspace changes. `mentionables` is @relay, the confirmed human members
 * and the advisors; `agents` is the workspace's active_roster (Team page
 * only — spawned build agents can't be messaged).
 *
 * `error` is set while the fetch is failing and cleared on the next good
 * response, so callers can tell "couldn't load" apart from "genuinely
 * empty"; `retry()` re-fetches immediately (track:small-fixes-1).
 */

import { useEffect, useRef, useState } from "react";
import { getAuthHeader } from "../lib/auth";

export interface Mentionable {
  name: string;
  kind: "relay" | "human" | "advisor";
  label?: string;
}

export interface MentionablesLists {
  mentionables: Mentionable[];
  agents: string[];
}

export interface MentionablesValue extends MentionablesLists {
  error: Error | null;
  retry: () => void;
}

const EMPTY: MentionablesLists = { mentionables: [], agents: [] };

export async function fetchMentionables(workspaceId: string): Promise<MentionablesLists> {
  const res = await fetch("/api/workspace/mentionables", {
    headers: await getAuthHeader(workspaceId),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  const mentionables = Array.isArray(data?.mentionables)
    ? data.mentionables.filter(
        (m: unknown): m is Mentionable =>
          !!m && typeof (m as Mentionable).name === "string" && typeof (m as Mentionable).kind === "string",
      )
    : [];
  const agents = Array.isArray(data?.agents)
    ? data.agents.filter((a: unknown): a is string => typeof a === "string")
    : [];
  return { mentionables, agents };
}

export function useMentionables(workspaceId: string): MentionablesValue {
  const [lists, setLists] = useState<MentionablesLists>(EMPTY);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Clear only when the workspace changes; a retry keeps the current list on
    // screen so a refresh after Add or Delete doesn't flash the empty state.
    if (loadedFor.current !== workspaceId) {
      loadedFor.current = workspaceId;
      setLists(EMPTY);
    }
    if (!workspaceId) return;
    (async () => {
      try {
        const next = await fetchMentionables(workspaceId);
        if (cancelled) return;
        setLists(next);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        const e = err instanceof Error ? err : new Error(String(err));
        console.warn("[useMentionables]", e.message);
        setError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, attempt]);

  return { ...lists, error, retry: () => setAttempt((n) => n + 1) };
}
